import { createAsyncThunk, createReducer } from "@reduxjs/toolkit";

import type { PaymentOptionId } from "~/common/constants";
import {
  type CourseId,
  CourseVersion,
  isAdditionalAlignersCourse,
  isRetainersCourse,
} from "~/common/courses";
import { fetchWithAuth } from "~/common/fetch";
import { API_COURSE_INSTALLMENT_GET } from "~/config";
import type { RootState } from "~/store";
import type { Nullable } from "~/types/common";
import type { TCourseOptionTag, TPatient } from "~/types/patient";
import type { MutableQueryState } from "~/types/redux";

export type TCourseInstallment = Record<CourseId, Partial<TPaymentOptionMap>>;

export type TPaymentOptionMap = Record<PaymentOptionId, number[] | TPaymentOptionInstallment[]> & {
  version: CourseVersion | null;
  /**
   * `true` if deep CBCT service is available for the course.
   */
  deep_cbct: boolean;
};

type TPaymentOptionInstallment = { tag: TCourseOptionTag; price: number; version: CourseVersion };

const initialState: MutableQueryState<TCourseInstallment> = {
  status: "idle",
  data: undefined,
  error: undefined,
};

export function isArrayOfNumbers(array: unknown[]): array is number[] {
  return array.every((value) => typeof value == "number");
}

export const getCourseInstallment = createAsyncThunk(
  "courseInstallment/fetch",
  async (patientId: TPatient["patient_id"] | undefined = undefined) => {
    const url = new URL(API_COURSE_INSTALLMENT_GET, window.location.origin);

    if (patientId) {
      url.searchParams.append("patient_id", patientId.toString());
    }

    const response = await fetchWithAuth(url.toString());

    if (!response.ok) {
      throw new Error(`Failed loading {GET_COURSE_INSTALLMENT_FAILURE}: ${response.statusText}`);
    }

    return response.json() as Promise<TCourseInstallment>;
  },
);

export const selectCourseInstallment = (state: RootState) => state.courseInstallment.data;

export const courseInstallment = createReducer(initialState, (builder) =>
  builder
    .addCase(getCourseInstallment.pending, (state) => {
      state.status = "loading";
    })
    .addCase(getCourseInstallment.fulfilled, (state, action) => {
      state.status = "success";
      state.data = action.payload;
      state.error = undefined;
    })
    .addCase(getCourseInstallment.rejected, (state, action) => {
      state.status = "error";
      state.data = undefined;
      state.error = action.error;
    }),
);

export function getCourseVersion(
  courseInstallment: TCourseInstallment,
  courseId: CourseId,
): Nullable<CourseVersion> {
  if (isRetainersCourse(courseId)) {
    return CourseVersion.V1;
  }

  return courseInstallment[courseId].version;
}

/**
 * Returns `true` if course includes deep CBCT.
 */
export function isCBCTIncludedInCourse(
  courseInstallment: TCourseInstallment,
  courseId: CourseId,
): boolean {
  if (
    !isCourseActive(courseInstallment, courseId) ||
    isRetainersCourse(courseId) ||
    isAdditionalAlignersCourse(courseId)
  ) {
    return false;
  }

  return courseInstallment[courseId].deep_cbct == false;
}

/**
 * Returns `true` if course allows to order deep CBCT as additional service.
 */
export function isCBCTServiceAvailableForCourse(
  courseInstallment: TCourseInstallment,
  courseId: CourseId,
): boolean {
  if (
    !isCourseActive(courseInstallment, courseId) ||
    isRetainersCourse(courseId) ||
    isAdditionalAlignersCourse(courseId)
  ) {
    return false;
  }

  return courseInstallment[courseId].deep_cbct == true;
}

function isCourseActive(courseInstallment: TCourseInstallment, courseId: CourseId): boolean {
  return courseInstallment[courseId].version != null;
}
